"use client";

import { useEffect, useRef } from "react";
import {
  shouldRefreshPortalSession,
  shouldTouchPortalSession,
} from "@/lib/portal-auth-shared";

export function PortalSessionKeepAlive({
  expiresAt,
  lastSeenAt,
}: {
  expiresAt?: number;
  lastSeenAt?: number;
}) {
  const expiresAtRef = useRef(expiresAt);
  const lastSessionTouchAtRef = useRef(lastSeenAt);

  useEffect(() => {
    let cancelled = false;

    async function touchPortalSession() {
      const now = Date.now();
      const touchDue =
        lastSessionTouchAtRef.current === undefined ||
        shouldTouchPortalSession(lastSessionTouchAtRef.current, now);
      const refreshDue =
        expiresAtRef.current === undefined ||
        shouldRefreshPortalSession(expiresAtRef.current, now);
      if (!touchDue && !refreshDue) {
        return;
      }

      try {
        const response = await fetch("/api/auth/session", {
          cache: "no-store",
          credentials: "same-origin",
          method: "POST",
        });

        if (!cancelled && response.status === 401) {
          window.location.assign("/login?status=expired");
          return;
        }

        if (response.ok) {
          const payload = (await response.json().catch(() => null)) as {
            expiresAt?: unknown;
          } | null;
          lastSessionTouchAtRef.current = now;
          if (typeof payload?.expiresAt === "number") {
            expiresAtRef.current = payload.expiresAt;
          }
        }
      } catch {
        // Keep protected pages usable if a background renewal attempt fails.
      }
    }

    void touchPortalSession();

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void touchPortalSession();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return null;
}
